import { useState, useEffect } from 'react';
import { Grid } from '../game/Grid';

export const useGravity = (
    gridRef: React.MutableRefObject<Grid | null>,
    setDebugInfo: (info: string) => void
) => {
    // 重力感应默认关闭，需要手动开启
    const [gravityEnabled, setGravityEnabled] = useState(false);
    
    // 重力向量状态，用于 UI 显示
    const [gravityVector, setGravityVector] = useState({ x: 0, y: 1 });
    // 模拟重力模式（用于本地调试）
    const [simulateGravity, setSimulateGravity] = useState(false);

    // 请求权限并启用/关闭重力感应
    const toggleGravity = async () => {
        if (gravityEnabled) {
            // 关闭重力感应
            setGravityEnabled(false);
            // 重置重力为默认向下
            if (gridRef.current) {
                gridRef.current.gravity.x = 0;
                gridRef.current.gravity.y = 1;
                setGravityVector({ x: 0, y: 1 });
            }
            return;
        }

        // 开启重力感应
        // 安全检查 DeviceOrientationEvent 是否存在
        if (typeof DeviceOrientationEvent !== 'undefined' && 
            typeof (DeviceOrientationEvent as any).requestPermission === 'function') {
            try {
                const permissionState = await (DeviceOrientationEvent as any).requestPermission();
                if (permissionState === 'granted') {
                    setGravityEnabled(true);
                    setSimulateGravity(false); // 互斥
                } else {
                    alert('需要重力感应权限才能适配方向');
                }
            } catch (e) {
                console.error(e);
                // 某些非 https 环境可能会报错，但如果是 Android 或旧 iOS 可能直接可用
                setGravityEnabled(true); 
                setSimulateGravity(false); // 互斥
            }
        } else {
            // 非 iOS 13+ 设备，或者不支持 requestPermission 的环境，通常直接可用
            setGravityEnabled(true);
            setSimulateGravity(false); // 互斥
        }
    };

    // 监听设备方向变化，更新重力
    useEffect(() => {
        if (!gravityEnabled) return;

        const handleOrientation = (event: DeviceOrientationEvent) => {
            if (!gridRef.current) return;
            
            const { beta, gamma } = event;
            
            setDebugInfo(`B:${beta?.toFixed(1)} G:${gamma?.toFixed(1)}`);
            
            if (beta === null || gamma === null) return;

            // x轴重力受 gamma 影响 (左右倾斜)
            // 提高灵敏度：25度即达到满重力
            const gx = Math.max(-1, Math.min(1, gamma / 25));
            
            // y轴重力受 beta 影响
            const gy = Math.max(-1, Math.min(1, beta / 25));

            const orientation = window.screen.orientation?.type || 'portrait-primary';
            
            // 计算最终应用的重力向量
            let finalGx = gx;
            let finalGy = gy;

            if (orientation.includes('landscape')) {
                 if (orientation === 'landscape-primary') {
                     // 左转90度 (Home键在右): 
                     // gamma(右倾) -> 屏幕上倾 -> y负
                     // beta(前倾) -> 屏幕左倾 -> x负
                     finalGx = -gy;
                     finalGy = -gx;
                 } else if (orientation === 'landscape-secondary') {
                     // 右转90度 (Home键在左):
                     // gamma(右倾) -> 屏幕下倾 -> y正
                     // beta(前倾) -> 屏幕右倾 -> x正
                     finalGx = gy;
                     finalGy = gx;
                 }
            }
            
            // 应用重力
            gridRef.current.gravity.x = finalGx;
            gridRef.current.gravity.y = finalGy;
            
            setGravityVector({ x: finalGx, y: finalGy });
            
            setDebugInfo(`B:${beta?.toFixed(0)} G:${gamma?.toFixed(0)} -> x:${finalGx.toFixed(1)} y:${finalGy.toFixed(1)}`);
        };

        window.addEventListener('deviceorientation', handleOrientation);
        return () => {
            window.removeEventListener('deviceorientation', handleOrientation);
        };
    }, [gravityEnabled]);

    // 模拟重力模式：鼠标位置控制重力
    useEffect(() => {
        if (!simulateGravity) return;

        const handlePointerMove = (e: PointerEvent) => {
            if (!gridRef.current) return;
            
            const cx = window.innerWidth / 2;
            const cy = window.innerHeight / 2;
            
            // 计算鼠标相对于中心的偏移，归一化到 [-1, 1]
            // 分母取屏幕宽高的较小值的一半，这样在圆形区域内能达到最大值
            const maxRadius = Math.min(window.innerWidth, window.innerHeight) / 2;
            
            let gx = (e.clientX - cx) / maxRadius;
            let gy = (e.clientY - cy) / maxRadius;
            
            // 限制范围
            gx = Math.max(-1, Math.min(1, gx));
            gy = Math.max(-1, Math.min(1, gy));
            
            // 增加一点死区，方便回正
            if (Math.abs(gx) < 0.1) gx = 0;
            if (Math.abs(gy) < 0.1) gy = 0;

            gridRef.current.gravity.x = gx;
            gridRef.current.gravity.y = gy;
            setGravityVector({ x: gx, y: gy });
            setDebugInfo(`Sim: x:${gx.toFixed(1)} y:${gy.toFixed(1)}`);
        };

        window.addEventListener('pointermove', handlePointerMove);
        return () => {
            window.removeEventListener('pointermove', handlePointerMove);
            // 退出模拟时重置重力
            if (gridRef.current) {
                gridRef.current.gravity.x = 0;
                gridRef.current.gravity.y = 1;
                setGravityVector({ x: 0, y: 1 });
            }
        };
    }, [simulateGravity]);

    // 重力关闭时重置 (Adding this back as it was in the original component logic)
    useEffect(() => {
        if (!gravityEnabled && gridRef.current && !simulateGravity) {
            gridRef.current.gravity.x = 0;
            gridRef.current.gravity.y = 1;
            setGravityVector({ x: 0, y: 1 });
        }
    }, [gravityEnabled, simulateGravity]);

    return {
        gravityEnabled,
        setGravityEnabled,
        gravityVector,
        simulateGravity,
        setSimulateGravity,
        toggleGravity
    };
};
